import {
  Settlement,
  SettlementDetails,
  SettlementGroup,
  WeightedDifference,
} from 'common/src/utils/types';
import { calculateMedian, getWeekKey } from 'common/src/utils/functions';

export function calculateClientDifference(
  settlement: Settlement,
  settlementsByGroup: SettlementGroup
): {
  weightedDiff: WeightedDifference;
  comparableKilos: number;
  comparableFob: number;
} {
  let weightedDiff: WeightedDifference = {
    byVolume: 0,
    byFob: 0,
  };
  let comparableKilos = 0;
  let comparableFob = 0;

  settlement.settlement_details?.forEach((detail) => {
    const isAir = settlement.ship_trip.ship.type === 'A';
    const isGround = settlement.ship_trip.ship.type === 'T';
    const groupKey = isAir
      ? `AIR-${getWeekKey(settlement.ship_trip.eta)}-M${settlement.market_id || 'UNKNOWN'}`
      : isGround
      ? `GROUND-${getWeekKey(settlement.ship_trip.eta)}`
      : `SHIP-${settlement.ship_trip.id}`;

    const comparableDetails = getComparableDetails(
      detail,
      settlement.receiver,
      settlementsByGroup[groupKey]
    );

    if (comparableDetails?.length > 0) {
      const { percentDiff } = calculatePercentageDifference(
        detail,
        comparableDetails
      );

      weightedDiff.byVolume += percentDiff * detail.kilos;
      weightedDiff.byFob += percentDiff * detail.usd_total_fob;

      comparableKilos += detail.kilos;
      comparableFob += detail.usd_total_fob;
    }
  });

  return { weightedDiff, comparableKilos, comparableFob };
}

function getComparableDetails(
  detail: SettlementDetails,
  currentClient: string,
  groupSettlements: Settlement[]
): number[] {
  return groupSettlements
    ?.map((s) => {
      // Skip settlements from the same client
      if (s.receiver === currentClient) return null;

      const matchingDetails =
        s.settlement_details?.filter(
          (d) =>
            d.variety === detail.variety &&
            d.caliber === detail.caliber &&
            d.color === detail.color &&
            `${d.format}-${d.unit_weight}KG` === `${detail.format}-${detail.unit_weight}KG` // Include format for client comparison
        ) || [];

      if (matchingDetails.length === 0) return null;

      const totalFob = matchingDetails.reduce(
        (sum, d) => sum + d.usd_total_fob,
        0
      );
      const totalKilos = matchingDetails.reduce((sum, d) => sum + d.kilos, 0);

      // Data validation: Ensure we have valid positive values
      if (totalKilos <= 0) {
        return null; // Skip invalid data
      }

      // Calculate CIF (FOB + freight from detail)
      const totalFreight = matchingDetails.reduce(
        (sum, d) => sum + d.usd_freight,
        0
      );
      const totalCif = totalFob + totalFreight;

      return totalCif / totalKilos;
    })
    .filter((value): value is number => value !== null);
}

function calculatePercentageDifference(
  detail: SettlementDetails,
  comparableDetails: number[]
): { percentDiff: number } {
  const median = calculateMedian(comparableDetails);

  // Data validation: Check for negative or zero kilos
  if (detail.kilos <= 0) {
    console.warn(`Data quality issue: Settlement detail ${detail.id} has invalid kilos value (${detail.kilos}). Skipping comparison.`);
    return { percentDiff: 0 };
  }

  // Calculate CIF per kilo for current detail (FOB + freight from detail)
  const fobPerKilo = detail.usd_total_fob / detail.kilos;
  const freightPerKilo = detail.usd_freight / detail.kilos;
  const currentCifKilo = fobPerKilo + freightPerKilo;

  // Handle the negative value scenarios properly
  let percentDiff: number;

  if (median === 0) {
    // If median is zero, we can't calculate a meaningful percentage
    return { percentDiff: 0 };
  }

  if (median > 0) {
    // Standard case: positive median
    percentDiff = ((currentCifKilo - median) / median) * 100;
  } else {
    // Negative median case: we need to flip the logic
    // When both are negative: more negative = worse performance
    // When current is positive and median negative: current is much better
    if (currentCifKilo >= 0) {
      // Current is positive/zero, median is negative -> current is much better
      // Calculate how much better as a positive percentage
      percentDiff = Math.abs(currentCifKilo - median) / Math.abs(median) * 100;
    } else {
      // Both are negative: closer to zero = better performance
      // More negative = worse performance
      percentDiff = ((currentCifKilo - median) / Math.abs(median)) * 100;
    }
  }

  return { percentDiff };
}
